import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../utils/api';
import Flag from '../components/Flag';
import { Send, ArrowDownLeft, Clock, ChevronRight, Bell, AlertTriangle, Shield, XCircle, Star, RefreshCw, Landmark, Ticket, Mail } from 'lucide-react';
import OnboardingTour from '../components/OnboardingTour';
import { BRAND } from '../brand';

const DashboardApp = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [balance, setBalance] = useState({ balance_clp: 0, balance_bob: 0 });
  const [recentTransfers, setRecentTransfers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showClp, setShowClp] = useState(true);
  const [promo, setPromo] = useState(null);
  const [gbPoints, setGbPoints] = useState(null);
  const [sorteo, setSorteo] = useState(null);

  // Billetera: gated a nivel marca. Si BRAND.features.wallet === false, la
  // billetera queda oculta en toda la instancia sin importar el flag por usuario.
  const walletEnabled = (BRAND?.features?.wallet !== false) && user?.wallet_enabled === true;

  const fetchData = useCallback(async () => {
    try {
      const requests = [api.get('/transfer-order/my-orders')];

      // Solo pedir saldo si tiene billetera activada
      if (walletEnabled) {
        requests.unshift(api.get('/wallet/balance'));
      }

      const results = await Promise.allSettled(requests);

      if (walletEnabled && results[0]?.status === 'fulfilled') {
        setBalance(results[0].value.data);
      }

      const transferIdx = walletEnabled ? 1 : 0;
      if (results[transferIdx]?.status === 'fulfilled') {
        const data = results[transferIdx].value.data;
        const orders = Array.isArray(data) ? data : (data.orders || data.transfers || []);
        setRecentTransfers(orders.slice(0, 5));
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [walletEnabled]);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    if (BRAND?.features?.gbPuntos) {
      api.get('/gb/promo/status').then(res => {
        if (res.data.active) setPromo(res.data);
      }).catch(() => {});
      api.get('/gb/status').then(res => {
        setGbPoints(res.data);
      }).catch(() => {});
    }
    if (BRAND?.features?.sorteo) {
      api.get('/sorteo/active').then(res => {
        if (res.data.active) setSorteo(res.data);
      }).catch(() => {});
    }
  }, []);

  const formatCurrency = (amount, currency) => {
    if (currency === 'BOB') return `Bs ${Math.round(amount).toLocaleString('es-BO')}`;
    return `$${Math.round(amount).toLocaleString('es-CL', { timeZone: 'America/Santiago' })}`;
  };

  const getStatusColor = (status) => {
    const colors = {
      completed: 'bg-green-100 text-green-700',
      processing: 'bg-blue-100 text-blue-700',
      payment_uploaded: 'bg-yellow-100 text-yellow-700',
      pending: 'bg-slate-100 text-slate-600',
      cancelled: 'bg-red-100 text-red-700',
      rejected: 'bg-red-100 text-red-700'
    };
    return colors[status] || 'bg-slate-100 text-slate-600';
  };

  const getStatusLabel = (status) => {
    const labels = {
      completed: 'Completado', processing: 'En proceso',
      payment_uploaded: 'Pago subido', pending: 'Pendiente',
      cancelled: 'Cancelado', rejected: 'Rechazado'
    };
    return labels[status] || status;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <img src="/logo-oficial.png" alt={`${BRAND.name}`} className="h-16 animate-pulse" />
      </div>
    );
  }

  const firstName = user?.full_name?.split(' ')[0] || 'Usuario';

  return (
    <div className="min-h-screen bg-slate-50 pb-40 lg:pb-8" data-testid="dashboard-app-root">
      {/* Header */}
      <div className="sticky top-0 z-40 bg-white/90 backdrop-blur-md border-b border-slate-100">
        <div className="max-w-md lg:max-w-5xl mx-auto px-4 lg:px-8 py-3 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <div
              className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 text-white font-manrope font-bold text-base"
              style={{ backgroundColor: 'var(--brand-primary)' }}
              aria-hidden="true"
              data-testid="dashboard-user-avatar"
            >
              {(firstName?.charAt(0) || 'U').toUpperCase()}
            </div>
            <div>
              <p className="text-slate-500 text-xs font-medium">Hola,</p>
              <h1 className="font-manrope text-lg font-bold text-slate-900 -mt-0.5" data-testid="dashboard-greeting">
                {firstName}
              </h1>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {BRAND?.features?.gbPuntos && gbPoints && user?.gb_active && (
              <button
                onClick={() => navigate('/gb-puntos')}
                className="flex items-center gap-1.5 bg-amber-50 hover:bg-amber-100 border border-amber-200 rounded-full px-3 py-1.5 transition active:scale-95"
                data-testid="header-gb-points"
              >
                <Star size={14} className="text-amber-500" fill="currentColor" />
                <span className="text-sm font-bold text-amber-700">{(gbPoints.points || 0).toLocaleString('es-CL', { timeZone: 'America/Santiago' })} {BRAND.puntosProgramName}</span>
              </button>
            )}
            <button 
              onClick={() => navigate('/notifications')} 
              className="p-2 rounded-full hover:bg-slate-100 transition relative"
              data-testid="notifications-btn"
            >
              <Bell size={20} className="text-slate-600" strokeWidth={1.5} />
            </button>
            <button 
              onClick={logout}
              className="lg:hidden text-xs text-slate-500 hover:text-red-500 font-medium px-2 py-1 rounded-lg hover:bg-slate-50 transition"
            >
              Salir
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-md lg:max-w-5xl mx-auto px-4 lg:px-8 pt-4 space-y-5">
        {/* KYC Rejected Banner */}
        {user && (user.kyc_status === 'rejected' || user.kyc_status === 'needs_resubmit') && (
          <div
            className="w-full bg-red-50 border border-red-200 rounded-2xl p-4"
            data-testid="kyc-rejected-banner"
          >
            <div className="flex items-center gap-3 mb-2">
              <div className="w-9 h-9 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                <XCircle size={18} className="text-red-600" />
              </div>
              <div className="flex-1">
                <p className="font-semibold text-red-800 text-sm">Verificación rechazada</p>
              </div>
            </div>
            {user.kyc_rejection_reason && (
              <p className="text-xs text-red-600 mb-3 ml-12">
                Motivo: {user.kyc_rejection_reason}
              </p>
            )}
            <button
              onClick={() => navigate('/kyc-verify')}
              className="w-full bg-red-600 text-white text-sm font-semibold py-2.5 rounded-xl transition-all hover:bg-red-700 active:scale-95"
              data-testid="kyc-retry-btn"
            >
              Repetir verificación KYC
            </button>
          </div>
        )}

        {/* KYC 2026 Banner - GRANDE para clientes sin KYC 2026 */}
        {user && !user.kyc_2026_completed && user.kyc_status !== 'rejected' && user.kyc_status !== 'needs_resubmit' && (() => {
          // Cliente HISTÓRICO: ya tenía KYC aprobado antes, solo necesita actualizar
          const isLegacyClient = user.kyc_status === 'approved';
          const firstName = (user.full_name || '').split(' ')[0] || '';
          return (
            <button
              onClick={() => navigate('/kyc-verify')}
              className={`w-full rounded-2xl p-5 transition-all hover:shadow-lg active:scale-[0.98] text-white ${
                isLegacyClient
                  ? 'bg-gradient-to-r from-emerald-500 to-teal-600'
                  : 'bg-gradient-to-r from-amber-500 to-orange-500'
              }`}
              data-testid="kyc-2026-banner"
            >
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
                  <Shield size={28} className="text-white" />
                </div>
                <div className="flex-1 text-left">
                  {isLegacyClient ? (
                    <>
                      <p className="font-bold text-white text-lg">Hola {firstName} 👋, gracias por seguir con nosotros</p>
                      <p className="text-sm text-white/90 mt-1 leading-snug">Por nuevas normas UAF de 2026, necesitamos actualizar tu identidad. Te toma menos de 1 minuto y solo lo haces 1 vez.</p>
                    </>
                  ) : (
                    <>
                      <p className="font-bold text-white text-lg">Actualizar KYC 2026</p>
                      <p className="text-sm text-white/80 mt-0.5">Tu verificación de identidad necesita ser actualizada para seguir operando</p>
                    </>
                  )}
                </div>
                <ChevronRight size={24} className="text-white/60" />
              </div>
              <div className="mt-3 bg-white/20 rounded-lg py-2 text-center">
                <p className="text-sm font-semibold text-white">
                  {isLegacyClient ? '✨ Tocar aquí para actualizar en 1 minuto' : 'Toca aqui para verificarte ahora'}
                </p>
              </div>
            </button>
          );
        })()}

        {/* Email Verification Banner */}
        {user && !user.email_verified && user.kyc_status !== 'rejected' && user.kyc_status !== 'needs_resubmit' && (
          <button
            onClick={() => navigate('/verify-email')}
            className="w-full bg-blue-50 border-2 border-blue-200 rounded-2xl p-4 transition-all hover:shadow-md active:scale-[0.98] text-left"
            data-testid="email-verify-banner"
          >
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                <Mail size={22} className="text-blue-600" />
              </div>
              <div className="flex-1">
                <p className="font-bold text-blue-800 text-sm">Verifica tu correo electrónico</p>
                <p className="text-xs text-blue-600 mt-0.5">
                  Confirma {user.email} para mayor seguridad en tu cuenta
                </p>
              </div>
              <ChevronRight size={18} className="text-blue-400 flex-shrink-0" />
            </div>
          </button>
        )}

        {/* PEP Status Banners */}
        {user?.is_pep && user?.pep_status === 'pending' && (
          <div
            className="w-full bg-amber-50 border-2 border-amber-300 rounded-2xl p-4"
            data-testid="pep-pending-banner"
          >
            <div className="flex items-start gap-3">
              <div className="w-11 h-11 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0 text-2xl">
                ⏳
              </div>
              <div className="flex-1">
                <p className="font-bold text-amber-900 text-sm">Cuenta PEP en revisión</p>
                <p className="text-xs text-amber-800 mt-1 leading-relaxed">
                  Por normativa antilavado, las Personas Políticamente Expuestas requieren
                  aprobación expresa antes de operar. Te avisaremos por WhatsApp cuando esté lista
                  (generalmente menos de 24hrs hábiles).
                </p>
              </div>
            </div>
          </div>
        )}
        {user?.is_pep && user?.pep_status === 'rejected' && (
          <div
            className="w-full bg-red-50 border-2 border-red-300 rounded-2xl p-4"
            data-testid="pep-rejected-banner"
          >
            <div className="flex items-start gap-3">
              <div className="w-11 h-11 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0 text-2xl">
                ⛔
              </div>
              <div className="flex-1">
                <p className="font-bold text-red-900 text-sm">Solicitud no aprobada</p>
                <p className="text-xs text-red-800 mt-1 leading-relaxed">
                  Lamentablemente no podemos habilitar tu cuenta en este momento. Si crees que
                  es un error, escribenos al WhatsApp para revisarlo.
                </p>
                <a
                  href={`https://wa.me/${BRAND?.landing?.footerWhatsAppNumber || ''}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center mt-2 bg-red-600 hover:bg-red-700 text-white font-bold text-xs px-3 py-1.5 rounded-lg"
                >
                  Contactar soporte
                </a>
              </div>
            </div>
          </div>
        )}

        {/* Sorteo Banner con imagen */}
        {BRAND?.features?.sorteo && sorteo && (
          <div className="w-full rounded-2xl overflow-hidden shadow-md" data-testid="sorteo-banner">
            {sorteo.sorteo.image_url && (
              <img 
                src={sorteo.sorteo.image_url} 
                alt={sorteo.sorteo.title}
                className="w-full object-cover"
                style={{ maxHeight: '200px' }}
              />
            )}
            <div className="bg-gradient-to-r from-green-600 to-green-700 px-4 py-2.5 flex items-center justify-between">
              <div className="flex items-center gap-2 text-white">
                <Ticket size={14} />
                <span className="text-xs font-bold">
                  Cada ${sorteo.sorteo.ticket_rule_amount?.toLocaleString('es-CL', { timeZone: 'America/Santiago' })} {sorteo.sorteo.ticket_rule_currency} = 1 boleto
                </span>
              </div>
              <span className="text-[10px] text-green-200 font-medium whitespace-nowrap">
                Termina: {sorteo.sorteo.end_date}
              </span>
            </div>
            {sorteo.my_tickets > 0 && (
              <div className="bg-green-50 px-4 py-1.5 text-center">
                <span className="text-xs font-bold text-green-800">
                  Tienes {sorteo.my_tickets} boleto{sorteo.my_tickets !== 1 ? 's' : ''}
                </span>
              </div>
            )}
          </div>
        )}

        {/* Promo Banner - solo si GB Puntos activo */}
        {BRAND?.features?.gbPuntos && promo && user?.gb_active && (
          <button
            onClick={() => navigate('/gb-puntos')}
            className="w-full flex items-center gap-3 bg-gradient-to-r from-yellow-50 to-amber-50 border-2 border-yellow-300 rounded-2xl p-3.5 transition-all hover:shadow-md active:scale-[0.98]"
            data-testid="promo-banner"
          >
            <div className="w-10 h-10 rounded-full bg-yellow-200 flex items-center justify-center flex-shrink-0 text-lg">
              <Star size={20} className="text-yellow-600" fill="currentColor" />
            </div>
            <div className="flex-1 text-left">
              <p className="font-bold text-amber-800 text-sm">Promo: +{promo.bonus_points} {BRAND.puntosProgramName}</p>
              <p className="text-xs text-amber-600">En tu primer envio desde Chile de $100.000+ CLP</p>
            </div>
            <ChevronRight size={18} className="text-amber-400" />
          </button>
        )}

        {/* Desktop two-column layout / Mobile single column */}
        <div className="lg:grid lg:grid-cols-5 lg:gap-6 space-y-5 lg:space-y-0">
          {/* Left column: Balance + Quick Actions */}
          <div className="lg:col-span-2 space-y-5">
            {/* Balance Card / Estado del usuario */}
            {walletEnabled ? (
              <div className="bg-gradient-to-br from-green-700 to-green-600 rounded-2xl p-5 text-white shadow-lg shadow-green-900/10 relative overflow-hidden">
                <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-white/5"></div>
                <div className="absolute -bottom-8 -left-8 w-32 h-32 rounded-full bg-white/5"></div>
                
                <p className="text-green-200 text-xs font-medium uppercase tracking-wider mb-1">Saldo disponible</p>
                
                <button 
                  onClick={() => setShowClp(!showClp)} 
                  className="text-left w-full"
                  data-testid="toggle-balance"
                >
                  <p className="font-manrope text-3xl font-extrabold tracking-tight">
                    {showClp 
                      ? `$${Math.round(balance.balance_clp).toLocaleString('es-CL', { timeZone: 'America/Santiago' })}`
                      : `Bs ${Math.round(balance.balance_bob).toLocaleString('es-BO')}`
                    }
                  </p>
                  <p className="text-green-200 text-xs mt-0.5">
                    {showClp ? 'Pesos Chilenos' : 'Bolivianos'} · Toca para cambiar
                  </p>
                </button>

                <div className="flex gap-2 mt-4">
                  <div className="flex items-center gap-1.5 bg-white/15 rounded-lg px-3 py-1.5">
                    <Flag country={showClp ? 'CL' : 'BO'} size={14} />
                    <span className="text-xs font-medium">{showClp ? 'CLP' : 'BOB'}</span>
                  </div>
                  {balance.balance_bob > 0 && showClp && (
                    <div className="flex items-center gap-1.5 bg-white/10 rounded-lg px-3 py-1.5">
                      <span className="text-xs text-green-200">También: Bs {Math.round(balance.balance_bob).toLocaleString()}</span>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="bg-gradient-to-br from-green-700 to-green-600 rounded-2xl p-5 text-white shadow-lg shadow-green-900/10 relative overflow-hidden">
                <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-white/5"></div>
                <div className="absolute -bottom-8 -left-8 w-32 h-32 rounded-full bg-white/5"></div>
                
                {!user?.is_approved ? (
                  <div className="relative">
                    <div className="flex items-center gap-3 mb-3">
                      <Clock size={24} className="text-green-200" />
                      <p className="font-manrope font-bold text-lg">Cuenta en revisión</p>
                    </div>
                    <p className="text-green-200 text-sm leading-relaxed">
                      Tu cuenta está siendo verificada por nuestro equipo. Te notificaremos cuando esté lista.
                    </p>
                    {!user?.kyc_2026_completed && (
                      <button 
                        onClick={() => navigate('/kyc-verify')}
                        className="mt-4 bg-white/20 hover:bg-white/30 text-white text-sm font-semibold py-2.5 px-4 rounded-xl transition-all active:scale-95 w-full"
                      >
                        Completa tu verificación para agilizar
                      </button>
                    )}
                  </div>
                ) : !user?.kyc_2026_completed ? (
                  <div className="relative">
                    <div className="flex items-center gap-3 mb-3">
                      <Shield size={24} className="text-green-200" />
                      <p className="font-manrope font-bold text-lg">Activa tu billetera</p>
                    </div>
                    <p className="text-green-200 text-sm leading-relaxed">
                      Completa la verificación de identidad para activar tu billetera y empezar a enviar dinero.
                    </p>
                    <button 
                      onClick={() => navigate('/kyc-verify')}
                      className="mt-4 bg-white/20 hover:bg-white/30 text-white text-sm font-semibold py-2.5 px-4 rounded-xl transition-all active:scale-95 w-full"
                    >
                      Verificar identidad
                    </button>
                  </div>
                ) : (
                  <div className="relative">
                    <div className="flex items-center gap-3 mb-3">
                      <Send size={24} className="text-green-200" />
                      <p className="font-manrope font-bold text-lg">
                        {recentTransfers.length === 0 ? 'Listo para enviar' : 'Envía a tu familia ahora'}
                      </p>
                    </div>
                    <p className="text-green-200 text-sm leading-relaxed">
                      {recentTransfers.length === 0
                        ? 'Tu cuenta está verificada. Haz tu primer envío.'
                        : 'Tasa en vivo · Acreditación en minutos.'}
                    </p>
                    <button 
                      onClick={() => navigate('/send-money')}
                      className="mt-4 bg-white/20 hover:bg-white/30 text-white text-sm font-semibold py-2.5 px-4 rounded-xl transition-all active:scale-95 w-full"
                    >
                      {recentTransfers.length === 0 ? 'Hacer mi primer envío' : 'Enviar dinero ahora'}
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Quick Actions */}
            <div className={`grid ${walletEnabled ? 'grid-cols-3' : 'grid-cols-2'} gap-3`} data-testid="quick-actions">
              <button
                onClick={() => navigate('/send-money')}
                className="bg-white border border-slate-100 rounded-2xl p-4 flex flex-col items-center gap-2.5 shadow-sm hover:border-green-200 hover:shadow-md transition-all active:scale-95"
                data-testid="action-send"
              >
                <div className="w-11 h-11 rounded-xl bg-green-50 flex items-center justify-center">
                  <Send size={20} className="text-green-700" strokeWidth={1.8} />
                </div>
                <span className="text-xs font-semibold text-slate-700">Enviar</span>
              </button>

              {walletEnabled && (
                <button
                  onClick={() => navigate('/recharge')}
                  className="bg-white border border-slate-100 rounded-2xl p-4 flex flex-col items-center gap-2.5 shadow-sm hover:border-green-200 hover:shadow-md transition-all active:scale-95"
                  data-testid="action-recharge"
                >
                  <div className="w-11 h-11 rounded-xl bg-blue-50 flex items-center justify-center">
                    <ArrowDownLeft size={20} className="text-blue-600" strokeWidth={1.8} />
                  </div>
                  <span className="text-xs font-semibold text-slate-700">Recargar</span>
                </button>
              )}

              {walletEnabled && (
                <button
                  onClick={() => navigate('/convert')}
                  className="bg-white border border-slate-100 rounded-2xl p-4 flex flex-col items-center gap-2.5 shadow-sm hover:border-indigo-200 hover:shadow-md transition-all active:scale-95"
                  data-testid="action-convert"
                >
                  <div className="w-11 h-11 rounded-xl bg-indigo-50 flex items-center justify-center">
                    <RefreshCw size={20} className="text-indigo-600" strokeWidth={1.8} />
                  </div>
                  <span className="text-xs font-semibold text-slate-700">Convertir</span>
                </button>
              )}

              <button
                onClick={() => navigate('/transactions')}
                className="bg-white border border-slate-100 rounded-2xl p-4 flex flex-col items-center gap-2.5 shadow-sm hover:border-green-200 hover:shadow-md transition-all active:scale-95"
                data-testid="action-history"
              >
                <div className="w-11 h-11 rounded-xl bg-purple-50 flex items-center justify-center">
                  <Clock size={20} className="text-purple-600" strokeWidth={1.8} />
                </div>
                <span className="text-xs font-semibold text-slate-700">Historial</span>
              </button>

              {BRAND?.features?.gbPuntos && user?.gb_active && (
              <button
                onClick={() => navigate('/gb-puntos')}
                className="bg-white border border-slate-100 rounded-2xl p-4 flex flex-col items-center gap-2.5 shadow-sm hover:border-yellow-200 hover:shadow-md transition-all active:scale-95"
                data-testid="action-gb-puntos"
              >
                <div className="w-11 h-11 rounded-xl bg-yellow-50 flex items-center justify-center">
                  <Star size={20} className="text-yellow-600" strokeWidth={1.8} />
                </div>
                <span className="text-xs font-semibold text-slate-700">Puntos</span>
              </button>
              )}

              {BRAND?.features?.credibol && (
              <button
                type="button"
                onClick={() => navigate('/credibol')}
                className="bg-white border border-slate-100 rounded-2xl p-4 flex flex-col items-center gap-2.5 shadow-sm relative hover:shadow-md transition"
                data-testid="action-credibol"
              >
                <span className="absolute -top-1 -right-1 bg-purple-500 text-white text-[9px] font-bold px-1.5 py-0.5 rounded-full">
                  Nuevo
                </span>
                <div className="w-11 h-11 rounded-xl bg-emerald-50 flex items-center justify-center">
                  <Landmark size={20} className="text-emerald-600" strokeWidth={1.8} />
                </div>
                <span className="text-xs font-semibold text-slate-700">CrediBol</span>
              </button>
              )}
            </div>
          </div>

          {/* Right column: Recent Activity */}
          <div className="lg:col-span-3">
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-manrope font-bold text-base text-slate-900">Actividad reciente</h2>
              <button 
                onClick={() => navigate('/transactions')}
                className="text-xs text-green-700 font-semibold hover:text-green-800"
              >
                Ver todo
              </button>
            </div>

            <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden divide-y divide-slate-50">
              {recentTransfers.length === 0 ? (
                <div className="p-8 text-center">
                  <Clock size={32} className="text-slate-300 mx-auto mb-2" />
                  <p className="text-slate-500 text-sm">Sin transferencias recientes</p>
                  <button 
                    onClick={() => navigate('/send-money')}
                    className="mt-3 text-green-700 font-semibold text-sm hover:text-green-800"
                  >
                    Hacer tu primer envío
                  </button>
                </div>
              ) : (
                recentTransfers.map((t) => {
                  const bd = Array.isArray(t.bank_details) ? t.bank_details?.[0] : t.bank_details;
                  const name = t.recipient_name || bd?.full_name || t.recipient_email || 'Envío';
                  const countryFlags = { 'BOB': '🇧🇴', 'ARS': '🇦🇷', 'PEN': '🇵🇪', 'VES': '🇻🇪', 'COP': '🇨🇴', 'CLP': '🇨🇱', 'BRL': '🇧🇷', 'USD': '🇺🇸' };
                  const flag = countryFlags[t.currency_to] || countryFlags[t.currency_from] || '🌎';
                  return (
                    <div key={t.id} onClick={() => navigate(t.id ? `/transfer-order/${t.id}` : '/transactions')}
                      className="px-4 py-3 flex items-center gap-3 hover:bg-slate-50 transition-colors cursor-pointer active:bg-slate-100">
                      <div className="w-9 h-9 rounded-full bg-slate-100 flex items-center justify-center flex-shrink-0 text-xl">
                        {flag}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-800 truncate">{name}</p>
                        <p className="text-xs text-slate-400">
                          {new Date(t.created_at).toLocaleDateString('es-CL', { day: 'numeric', month: 'short', timeZone: 'America/Santiago' })}
                          {' · '}
                          <span className={`font-medium ${
                            t.status === 'completed' ? 'text-green-600' :
                            t.status === 'rejected' ? 'text-red-600' : 'text-blue-600'
                          }`}>{getStatusLabel(t.status)}</span>
                        </p>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <p className="text-sm font-bold" style={{color: '#008000'}}>
                          {formatCurrency(t.amount_from || t.amount_to, t.currency_from || 'CLP')}
                        </p>
                        <p className="text-[10px] text-slate-400">
                          {formatCurrency(t.amount_to || t.amount_from, t.currency_to || 'BOB')}
                        </p>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Navigation removed - handled by ClientLayout */}

      {/* Tour de bienvenida - solo primera vez */}
      <OnboardingTour />
    </div>
  );
};

export default DashboardApp;
